import { createStore } from "solid-js/store";
/**
 * Multi-Factor Authentication
 */
export class MFA {
    #client;
    #store;
    /**
     * Construct MFA helper
     * @param client Client
     * @param state State
     */
    constructor(client, state) {
        this.#client = client;
        this.#store = createStore(state);
    }
    /**
     * Whether authenticator app is enabled
     */
    get authenticatorEnabled() {
        return this.#store[0].totp_mfa ?? false;
    }
    /**
     * Whether recovery codes are enabled
     */
    get recoveryEnabled() {
        return this.#store[0].recovery_active ?? false;
    }
    /**
     * Available MFA methods for generating tickets
     */
    get availableMethods() {
        return this.authenticatorEnabled
            ? this.recoveryEnabled
                ? ["Totp", "Recovery"]
                : ["Totp"]
            : ["Password"];
    }
    /**
     * Create an MFA ticket
     * @param params
     * @returns Token
     */
    async createTicket(params) {
        return new MFATicket(this.#client, await this.#client.api.put("/auth/mfa/ticket", params), this.#store[1]);
    }
    /**
     * Enable authenticator using token generated from secret found earlier
     * @param token Token
     */
    async enableAuthenticator(token) {
        await this.#client.api.put("/auth/mfa/totp", { totp_code: token });
        this.#store[1]("totp_mfa", true);
    }
}
/**
 * MFA Ticket
 */
export class MFATicket {
    #client;
    #ticket;
    #mutate;
    #used = false;
    /**
     * Construct MFA Ticket
     * @param client Client
     * @param ticket Ticket
     * @param mutate Mutate the store
     */
    constructor(client, ticket, mutate) {
        this.#client = client;
        this.#ticket = ticket;
        this.#mutate = mutate;
    }
    /**
     * Token
     */
    get token() {
        return this.#ticket.token;
    }
    /**
     * @internal
     * Use this ticket
     */
    _consume() {
        if (this.#used)
            throw "Already used this ticket!";
        this.#used = true;
    }
    /**
     * Fetch recovery codes
     * @returns List of codes
     */
    fetchRecoveryCodes() {
        this._consume();
        return this.#client.api.post("/auth/mfa/recovery", undefined, {
            headers: {
                "X-MFA-Ticket": this.token,
            },
        });
    }
    /**
     * Generate new set of recovery codes
     * @returns List of codes
     */
    async generateRecoveryCodes() {
        this._consume();
        const codes = await this.#client.api.patch("/auth/mfa/recovery", undefined, {
            headers: {
                "X-MFA-Ticket": this.token,
            },
        });
        this.#mutate("recovery_active", true);
        return codes;
    }
    /**
     * Generate new authenticator secret
     * @returns Secret
     */
    async generateAuthenticatorSecret() {
        this._consume();
        return (await this.#client.api.post("/auth/mfa/totp", undefined, {
            headers: {
                "X-MFA-Ticket": this.token,
            },
        })).secret;
    }
    /**
     * Disable authenticator
     */
    async disableAuthenticator() {
        this._consume();
        await this.#client.api.delete("/auth/mfa/totp", undefined, {
            headers: {
                "X-MFA-Ticket": this.token,
            },
        });
        this.#mutate("totp_mfa", false);
    }
    /**
     * Disable account
     */
    disableAccount() {
        this._consume();
        return this.#client.api.post("/auth/account/disable", undefined, {
            headers: {
                "X-MFA-Ticket": this.token,
            },
        });
    }
    /**
     * Delete account
     */
    deleteAccount() {
        this._consume();
        return this.#client.api.post("/auth/account/delete", undefined, {
            headers: {
                "X-MFA-Ticket": this.token,
            },
        });
    }
    /**
     * Delete server
     */
    async deleteServer(server) {
        if (!(server.owner?.self ?? server.ownerId === this.#client.user?.id))
            throw new Error("NotOwner");
        this._consume();
        return this.#client.api.delete(`/servers/${server.id}`, undefined, {
            headers: {
                "X-MFA-Ticket": this.token,
            },
        });
    }
    /**
     * Delete bot
     */
    async deleteBot(bot) {
        if (!(bot.owner?.self ?? bot.ownerId === this.#client.user?.id))
            throw new Error("NotOwner");
        this._consume();
        await this.#client.api.delete(`/bots/${bot.id}`, undefined, {
            headers: {
                "X-MFA-Ticket": this.token,
            },
        });
        this.#client.bots.delete(bot.id);
    }
}
